import { useCallback, useEffect, useRef, useState } from 'react';
import { saveService } from '../../services/SaveService';
import { mapService } from '../../services/MapService';
import { dungeonService } from '../../services/DungeonService';
import { closeInterface } from '../../services/InterfaceService';
import { useMenuNavigation } from '../../hooks/menu/useMenuNavigation';
import { useGameStore } from '../../store/gameSlice';
import { useDevtoolStore } from '../../store/devtoolSlice';
import { KeyDisplay } from './KeyDisplay';
import {
  drawDungeonMap,
  MAP_CATEGORY_STYLE,
  MONSTER_ICON_STYLE,
  DiscoveredKinds,
} from '../../utils/dungeon-map-draw';
import './MapScreen.scss';

const ICON_LABEL: Record<string, string> = {
  elite: 'Élite',
  miniboss: 'Mini-boss',
  boss: 'Boss',
};
const CAT_LABEL: Record<string, string> = {
  exit: 'Sortie',
  chest: 'Coffre',
  shop: 'Boutique',
  altar: 'Autel',
  spring: 'Source',
  trap: 'Piège',
};

export const MapScreen = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const screenWidth = useGameStore((s) => s.screenWidth);
  const screenHeight = useGameStore((s) => s.screenHeight);
  const currentFloor = useGameStore((s) => s.currentFloor);
  const currentDungeonId = useGameStore((s) => s.currentDungeonId);
  const devMode = useDevtoolStore((s) => s.devModeEnabled);

  const [kinds, setKinds] = useState<DiscoveredKinds>({
    categories: new Set(),
    icons: new Set(),
  });

  const close = useCallback(() => {
    closeInterface();
  }, []);

  useMenuNavigation({
    disabled: false,
    onCancel: close,
  });

  useEffect(() => {
    const draw = () => {
      const canvas = canvasRef.current;
      const mapData = saveService.getMapData();
      if (!canvas || !mapData) {
        return;
      }
      const { width, height } = mapData;

      const maxW = screenWidth * 0.9;
      const maxH = screenHeight * 0.85;
      const px = Math.max(4, Math.floor(Math.min(maxW / width, maxH / height)));

      canvas.width = width * px;
      canvas.height = height * px;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      const found = drawDungeonMap({
        ctx,
        pixelSize: px,
        revealAll: devMode,
        showGlyphs: true,
        showRoomDebug: devMode,
      });

      if (devMode) {
        const rooms = mapService.getRooms();
        ctx.font = `bold ${Math.max(8, px)}px monospace`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.lineWidth = 3;
        rooms.forEach((room, index) => {
          const c = room.cells[Math.floor(room.cells.length / 2)];
          if (!c) {
            return;
          }
          const tx = c.x * px + px / 2;
          const ty = (height - 1 - c.y) * px + px / 2;
          ctx.strokeStyle = '#000';
          ctx.strokeText(String(index), tx, ty);
          ctx.fillStyle = '#fff';
          ctx.fillText(String(index), tx, ty);
        });
        ctx.textAlign = 'start';
        ctx.textBaseline = 'alphabetic';
      }

      setKinds(found);
    };

    draw();
    const interval = setInterval(draw, 250);
    return () => clearInterval(interval);
  }, [screenWidth, screenHeight, devMode]);

  const debug = devMode ? mapService.getGenerationDebug() : null;

  return (
    <div className="map-screen">
      <div className="map-screen-header">
        <span className="map-screen-title">Carte — Étage {currentFloor}</span>
      </div>
      <canvas ref={canvasRef} />
      {devMode && (
        <pre className="map-screen-debug">
          {`dungeon: ${currentDungeonId ?? '?'}\n`}
          {debug
            ? `style: ${debug.style}  ${debug.width}×${debug.height}\n` +
              `rooms: ${debug.rooms.length} (target ${debug.targetCount}, placed ${debug.placedCount}, dropped ${debug.droppedCount})  corridors: ${debug.corridorCount}\n` +
              debug.rooms
                .map(
                  (r) =>
                    `  #${r.index} ${r.family}${r.templateId ? ` (${r.templateId})` : ''} ` +
                    `bbox (${r.bbox.x},${r.bbox.y}) ${r.bbox.width}×${r.bbox.height} cells ${r.cellCount} center (${r.center.x},${r.center.y})`,
                )
                .join('\n')
            : 'gen debug N/A (reloaded floor — room overlay still shown)'}
          {`\nlocations:\n` +
            dungeonService.locationService
              .getPlacedLocations()
              .map((l) => `  ${l.id} @(${l.x},${l.y}) ${l.placement}`)
              .join('\n')}
        </pre>
      )}
      <div className="map-screen-legend">
        <span>
          <span className="map-screen-dot map-screen-dot--player" /> Joueur
        </span>
        {kinds.icons.has('normal') && (
          <span>
            <span className="map-screen-dot map-screen-dot--monster" /> Monstre
          </span>
        )}
        {(['elite', 'miniboss', 'boss'] as const)
          .filter((t) => kinds.icons.has(t))
          .map((t) => (
            <span key={t}>
              <span
                className={`map-screen-mk map-screen-mk--${t}`}
                style={
                  { '--mk': MONSTER_ICON_STYLE[t].color } as React.CSSProperties
                }
              />{' '}
              {ICON_LABEL[t]}
            </span>
          ))}
        {(['exit', 'chest', 'shop', 'altar', 'spring', 'trap'] as const)
          .filter((c) => kinds.categories.has(c))
          .map((c) => (
            <span key={c}>
              <span
                className="map-screen-glyph"
                style={{ color: MAP_CATEGORY_STYLE[c].color }}
              >
                {MAP_CATEGORY_STYLE[c].glyph}
              </span>{' '}
              {CAT_LABEL[c]}
            </span>
          ))}
      </div>
      <div className="map-screen-hint">
        <KeyDisplay context="interface" action="CANCEL" size="sm" /> pour fermer
      </div>
    </div>
  );
};
